import { Box, Typography, Button, Dialog, DialogContent, IconButton, Card, CircularProgress, Paper } from "@mui/material";
import PaTextField from "components/field/PaTextField";
import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import MobileId from "assets/mobile.svg";
import SmartId from "assets/smart-id.svg";
import * as yup from "yup";
import { isNumeric } from "util/UtilityFunctions";
import TimerComponent from "./SmartTimer";
import { useAppSelector } from "redux/hooks";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { createApi } from "util/ApiCreator";
import { SigningController } from "api/main/SigningController";
import CloseIcon from '@mui/icons-material/Close';
import { IdentificationController } from "api/main/IdentificationController";
import { adminTheme } from "theme/adminTheme";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const REFRESH_RATE_MILLI_SECONDS = 3000;

interface Props {
    type: 'PIN1' | 'PIN2';
    isConsent?: boolean;
    stepExecutionId: number;
    refresh: () => Promise<void>;
    handleSuccess: () => void;
    handleClose: () => void;
    open: boolean;
}

interface BoxConfigI {
    loadingCode?: string;
    error?: string;
    mode:
    | "CHOOSE"
    | "CONSENT"
    | "SMART_ID"
    | "MOBILE_ID"
    | "SUCCESS"
    | "ERROR"
    | "LOADING"
    | "INPUT";
    chosen: "SMART_ID" | "MOBILE_ID";
}

export default function VerySmartMarkId(props: Props) {

    useEffect(() => {
        setDefaultConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const setDefaultConfig = () => {
        if (props.isConsent) {
            setBoxConfig({
                error: undefined,
                mode: "CONSENT",
                chosen: "SMART_ID",
            });
        } else {
            setBoxConfig({
                error: undefined,
                mode: "CHOOSE",
                chosen: "SMART_ID",
            });
        }
    }

    const identificationApi = useRef(createApi("identification") as IdentificationController);
    const signingApi = useRef(createApi("signing") as SigningController);
    const me = useAppSelector((s) => s.me.info);

    const handleClose = async () => {
        if (boxConfig.mode === "SUCCESS") {
            props.handleSuccess();
            props.handleClose();
        }

        if (boxConfig.mode === "LOADING") {
            return;
        }

        setDefaultConfig();
        props.handleClose();
    };

    const [controlCode, setControlCode] = useState<string>();
    const [personCode, setPersonCode] = useState<string>();
    const [phone, setPhone] = useState<string>();
    const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();
    const [boxConfig, setBoxConfig] = useState<BoxConfigI>({
        error: undefined,
        mode: "CHOOSE",
        chosen: "SMART_ID",
    });

    function setModeType(chosen: "SMART_ID" | "MOBILE_ID"): void {
        setBoxConfig({ ...boxConfig, chosen: chosen });
    }

    function setMode(
        mode:
            | "CHOOSE"
            | "CONSENT"
            | "SMART_ID"
            | "MOBILE_ID"
            | "SUCCESS"
            | "ERROR"
            | "LOADING"
    ): void {
        setBoxConfig({ ...boxConfig, mode: mode });
    }

    const handleSubmit = async (obj: any) => {
        setMode("LOADING");
        setPersonCode(obj?.personCode);
        setPhone(obj?.phone);
    };

    const getContent = () => {
        if (boxConfig.mode === "CONSENT") {
            return (
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            width: 96,
                            height: 96,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${adminTheme.colors.primary}20 0%, ${adminTheme.colors.primary}10 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 3,
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 48, color: adminTheme.colors.primary }} />
                    </Box>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={() => handleSubmit({})}
                        sx={{
                            background: adminTheme.gradients.primary,
                            "&:hover": { background: adminTheme.gradients.primaryHover },
                            py: 1.5,
                            fontWeight: 600,
                        }}
                    >
                        Patvirtinu, kad perskaičiau dokumentą ir su juo susipažinau
                    </Button>
                </Box>
            );
        }

        if (boxConfig.mode === "CHOOSE") {
            return (
                <Box>
                    {/* Header */}
                    <Box sx={{
                        background: `linear-gradient(135deg, ${adminTheme.colors.primary}08 0%, ${adminTheme.colors.background} 100%)`,
                        p: 3,
                        borderBottom: `1px solid ${adminTheme.colors.border}`,
                        position: "relative",
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary, textAlign: "center" }}>
                            {props.type === "PIN1" && <>Pasirinkite tapatybės patvirtinimo būdą</>}
                            {props.type === "PIN2" && <>Pasirinkite pasirašymo būdą</>}
                        </Typography>
                        <IconButton
                            onClick={() => handleClose()}
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                bgcolor: adminTheme.colors.backgroundAlt,
                                "&:hover": { bgcolor: adminTheme.colors.border },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        {/* Method Selection Cards */}
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
                            <Card
                                onClick={() => setModeType("SMART_ID")}
                                sx={{
                                    p: 3,
                                    cursor: "pointer",
                                    border: boxConfig.chosen === "SMART_ID"
                                        ? `3px solid ${adminTheme.colors.primary}`
                                        : `2px solid ${adminTheme.colors.border}`,
                                    borderRadius: adminTheme.borderRadius.lg,
                                    transition: adminTheme.transitions.normal,
                                    bgcolor: boxConfig.chosen === "SMART_ID"
                                        ? `${adminTheme.colors.primary}08`
                                        : adminTheme.colors.background,
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: adminTheme.shadows.cardHover,
                                        borderColor: adminTheme.colors.primary,
                                    },
                                }}
                            >
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                }}>
                                    <Box
                                        component="img"
                                        src={SmartId}
                                        alt="Smart-ID"
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            filter: boxConfig.chosen === "SMART_ID" ? "none" : "grayscale(50%)",
                                            transition: adminTheme.transitions.normal,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: "center",
                                            fontWeight: boxConfig.chosen === "SMART_ID" ? 600 : 400,
                                            color: boxConfig.chosen === "SMART_ID"
                                                ? adminTheme.colors.primary
                                                : adminTheme.colors.textSecondary,
                                        }}
                                    >
                                        {props.type === "PIN1" && <>Identifikacija su SMART-ID programėle</>}
                                        {props.type === "PIN2" && <>Pasirašymas su SMART-ID programėle</>}
                                    </Typography>
                                </Box>
                            </Card>

                            <Card
                                onClick={() => setModeType("MOBILE_ID")}
                                sx={{
                                    p: 3,
                                    cursor: "pointer",
                                    border: boxConfig.chosen === "MOBILE_ID"
                                        ? `3px solid ${adminTheme.colors.primary}`
                                        : `2px solid ${adminTheme.colors.border}`,
                                    borderRadius: adminTheme.borderRadius.lg,
                                    transition: adminTheme.transitions.normal,
                                    bgcolor: boxConfig.chosen === "MOBILE_ID"
                                        ? `${adminTheme.colors.primary}08`
                                        : adminTheme.colors.background,
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: adminTheme.shadows.cardHover,
                                        borderColor: adminTheme.colors.primary,
                                    },
                                }}
                            >
                                <Box sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                }}>
                                    <Box
                                        component="img"
                                        src={MobileId}
                                        alt="Mobile-ID"
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            filter: boxConfig.chosen === "MOBILE_ID" ? "none" : "grayscale(50%)",
                                            transition: adminTheme.transitions.normal,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: "center",
                                            fontWeight: boxConfig.chosen === "MOBILE_ID" ? 600 : 400,
                                            color: boxConfig.chosen === "MOBILE_ID"
                                                ? adminTheme.colors.primary
                                                : adminTheme.colors.textSecondary,
                                        }}
                                    >
                                        {props.type === "PIN1" && <>Identifikacija su MOBILE-ID</>}
                                        {props.type === "PIN2" && <>Pasirašymas su MOBILE ID</>}
                                    </Typography>
                                </Box>
                            </Card>
                        </Box>

                        {/* Form */}
                        <Formik
                            initialValues={{
                                personCode: me?.personCode,
                                phone: me?.phone,
                            }}
                            onSubmit={handleSubmit}
                            validationSchema={boxConfig.chosen === "MOBILE_ID" ? getValidationSchemaForMobileId() : getValidationSchemaForSmartId()}
                        >
                            {({ isSubmitting }) => (
                                <Form>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                        <PaTextField
                                            name="personCode"
                                            label="Asmens kodas"
                                            fullWidth
                                            required
                                        />
                                        {boxConfig.chosen === "MOBILE_ID" && (
                                            <PaTextField
                                                name="phone"
                                                label="Telefono nr."
                                                fullWidth
                                                required
                                            />
                                        )}
                                        <Button
                                            variant="contained"
                                            size="large"
                                            fullWidth
                                            disabled={isSubmitting}
                                            type="submit"
                                            sx={{
                                                background: adminTheme.gradients.primary,
                                                "&:hover": { background: adminTheme.gradients.primaryHover },
                                                py: 1.5,
                                                fontWeight: 600,
                                                mt: 1,
                                            }}
                                        >
                                            {props.type === "PIN1" && <>Patvirtinti tapatybę</>}
                                            {props.type === "PIN2" && <>Pradėti pasirašymą</>}
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Box>
                </Box>
            );
        }

        if (boxConfig.mode === "SUCCESS") {
            return (
                <Box sx={{ p: 5, textAlign: "center", position: "relative" }}>
                    <IconButton
                        onClick={() => handleClose()}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: adminTheme.colors.backgroundAlt,
                            "&:hover": { bgcolor: adminTheme.colors.border },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${adminTheme.colors.success}20 0%, ${adminTheme.colors.success}10 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 3,
                        }}
                    >
                        <CheckCircleIcon sx={{ fontSize: 64, color: adminTheme.colors.success }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: adminTheme.colors.textPrimary }}>
                        {props.type === "PIN1" && <>Jūs sėkmingai atlikote tapatybės patvirtinimą!</>}
                        {props.type === "PIN2" && <>Dokumentas sėkmingai pasirašytas!</>}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => handleClose()}
                        sx={{
                            background: adminTheme.gradients.success,
                            "&:hover": { opacity: 0.9 },
                            px: 5,
                            py: 1.5,
                            fontWeight: 600,
                            mt: 2,
                        }}
                    >
                        Tęsti toliau
                    </Button>
                </Box>
            );
        }

        if (boxConfig.mode === "LOADING") {
            if (props.isConsent) {
                return (
                    <Box sx={{ p: 5, textAlign: "center" }}>
                        <CircularProgress size={64} sx={{ color: adminTheme.colors.primary, mb: 3 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
                            Užklausa vykdoma, prašome palaukti
                        </Typography>
                        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mt: 1 }}>
                            Kraunasi...
                        </Typography>
                    </Box>
                );
            }

            return (
                <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ mb: 3, color: adminTheme.colors.textPrimary, fontWeight: 500 }}>
                        Įsitikinkite, kad kodas pateiktas žemiau atitinka kodą esantį jūsų įrenginyje. Atitikus kodui, įveskite {props.type}
                    </Typography>

                    <Paper
                        sx={{
                            p: 4,
                            mb: 3,
                            background: `linear-gradient(135deg, ${adminTheme.colors.primary}12 0%, ${adminTheme.colors.primary}08 100%)`,
                            border: `2px solid ${adminTheme.colors.primary}30`,
                            borderRadius: adminTheme.borderRadius.lg,
                        }}
                    >
                        <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>
                            Kontrolinis kodas
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: adminTheme.colors.primary, mt: 1 }}>
                            {controlCode || "Kraunasi..."}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 2 }}>
                            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                                Kodas nebegalios po:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.warning }}>
                                <TimerComponent />s
                            </Typography>
                        </Box>
                    </Paper>

                    <CircularProgress size={48} sx={{ color: adminTheme.colors.primary, mb: 2 }} />
                    <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mb: 3 }}>
                        Laukiama Jūsų patvirtinimo
                    </Typography>

                    {!controlCode && (
                        <Paper
                            sx={{
                                p: 2,
                                bgcolor: `${adminTheme.colors.warning}10`,
                                border: `1px solid ${adminTheme.colors.warning}40`,
                                borderRadius: adminTheme.borderRadius.md,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: adminTheme.colors.warning, fontWeight: 600 }}>
                                SMART-ID atveju, kodas pasirodys tik atvėrus SMART-ID programėlę
                            </Typography>
                        </Paper>
                    )}
                </Box>
            );
        }

        if (boxConfig.mode === "ERROR") {
            return (
                <Box sx={{ p: 5, textAlign: "center", position: "relative" }}>
                    <IconButton
                        onClick={() => handleClose()}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: adminTheme.colors.backgroundAlt,
                            "&:hover": { bgcolor: adminTheme.colors.border },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    <Box
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${adminTheme.colors.error}20 0%, ${adminTheme.colors.error}10 100%)`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 3,
                        }}
                    >
                        <ErrorOutlineIcon sx={{ fontSize: 64, color: adminTheme.colors.error }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: adminTheme.colors.textPrimary }}>
                        Įvyko klaida
                    </Typography>
                    <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mb: 3 }}>
                        Prašome pabandyti iš naujo
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => handleClose()}
                        sx={{
                            bgcolor: adminTheme.colors.error,
                            "&:hover": { bgcolor: adminTheme.colors.error, opacity: 0.9 },
                            px: 5,
                            py: 1.5,
                            fontWeight: 600,
                        }}
                    >
                        Uždaryti
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="error">
                    Nežinomas režimas
                </Typography>
            </Box>
        );
    };

    useEffect(() => {
        loadingModeInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boxConfig.mode]);

    const getFuncs = () => {
        if (props.type === 'PIN1') {
            if (boxConfig.chosen === 'SMART_ID')
                return { init: identificationApi.current.initSmartIdLogin, poll: identificationApi.current.pollSmartId };

            if (boxConfig.chosen === 'MOBILE_ID')
                return { init: identificationApi.current.initMobileIdLogin, poll: identificationApi.current.pollMobileId };
        }

        if (props.type === 'PIN2') {
            if (boxConfig.chosen === 'SMART_ID')
                return { init: signingApi.current.initiateSigning, poll: signingApi.current.checkStatusOfSigning };

            if (boxConfig.chosen === 'MOBILE_ID')
                return { init: signingApi.current.initiateSigning, poll: signingApi.current.checkStatusOfSigning };
        }
    }

    const loadingModeInit = async () => {
        if (boxConfig.mode === "SUCCESS") {
            if (intervalId) {
                clearInterval(intervalId);
            }
            return;
        }

        if (boxConfig.mode !== "LOADING")
            return;

        let funcs = getFuncs();
        if (!funcs?.init) {
            return;
        }

        if (!props.stepExecutionId)
            return;

        let obj = {
            personCode,
            phone,
            stepExecutionId: props.stepExecutionId,
            type: boxConfig.chosen
        } as any;

        let sessionId = await funcs?.init(obj);

        if (props.isConsent) {
            setMode("SUCCESS");
            // await props.refresh();
            return;
        }

        let id = setInterval(async () => {

            let polls = {
                sessionId,
                stepExecutionId: props.stepExecutionId,
            } as any;

            const response = await funcs?.poll(polls)
                .catch((error: any) => {
                    clearInterval(id);
                    setMode("ERROR");
                });

            if (!response) return;

            setControlCode(response.control_code);

            if (response.status === "ok") {
                clearInterval(id);
                setMode("SUCCESS");
            }

            // await props.refresh();
        }, REFRESH_RATE_MILLI_SECONDS);

        setIntervalId(id);
    }

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: adminTheme.borderRadius.xl,
                    overflow: "hidden",
                    boxShadow: adminTheme.shadows.cardHover,
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                {getContent()}
            </DialogContent>
        </Dialog>
    );
}


function createCommonValidationSchema() {
    return yup.object({
        personCode: yup
            .string()
            .length(11, "Asmens kodas privalo turėti 11 skaičių")
            .test(
                "isNumeric",
                "Asmens kodą privalo sudaryti tik skaitmenys",
                (value) => isNumeric(value),
            )
            .test(
                "fisrtNumberMustBeCorrect",
                "Asmens kodas privalo prasidėti 3, 4, 5 arba 6 skaitmenimis",
                (value) =>
                    value?.charAt(0) === "3" ||
                    value?.charAt(0) === "4" ||
                    value?.charAt(0) === "5" ||
                    value?.charAt(0) === "6",
            )
            .required("Asmens kodas yra privalomas"),
    });
}

function getValidationSchemaForSmartId() {
    return createCommonValidationSchema();
}

function getValidationSchemaForMobileId() {
    return yup.object({
        phone: yup
            .string()
            .matches(/^\+[0-9]{11}$/, {
                message:
                    "Numeris turi prasidėti + simboliu ir numerį privalo sudaryti 11 skaitmenų",
                excludeEmptyString: true,
            })
            .required("Numeris yra privalomas"),
        ...createCommonValidationSchema().fields,
    });
}